'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback, useTransition } from 'react';
import { useSession } from 'next-auth/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faHiveHeartLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import PageTitle from '@/app/_components/PageTitle';
import AddFavoritesListModal from './AddFavoritesListModal';
import FavoritesCard from './FavoritesCard';
import getCustomerFavoritesAction from '@/app/_lib/actions/GetCustomerFavoritesAction';
import deleteFavoritesListAction from '@/app/_lib/actions/DeleteFavoritesListAction';
import { useGlobalMessage, useFavorites } from '@/app/_lib/hooks';
const FADE_OUT_DURATION = 300; // milliseconds - matches Tailwind duration-300
const FADE_IN_DURATION = 700; // milliseconds - matches Tailwind duration-700
const PRE_ANIMATION_DELAY = 1000; // delay after modal closes before animation starts

const FavoritesList = () => {
  const $ = _c(41);
  const addGlobalMessage = useGlobalMessage();
  const {
    data: session
  } = useSession();
  const {
    favorites: favoriteIds
  } = useFavorites();
  const [favorites, setFavorites] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [animatingProducts, setAnimatingProducts] = useState(t0);
  const [addFavoritesListModalOpen, setAddFavoritesListModalOpen] = useState(false);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [favoritesListModalData, setFavoritesListModalData] = useState(t1);
  const [, startTransition] = useTransition();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = data => {
      setFavoritesListModalData(data);
      setAddFavoritesListModalOpen(true);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const openAddFavoritesListModal = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setAddFavoritesListModalOpen(false);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const closeAddFavoritesListModal = t3;
  let t4;
  if ($[4] !== startTransition) {
    t4 = async () => {
      const favoritesResponse = await getCustomerFavoritesAction({
        with_products: true
      });
      if (favoritesResponse.success) {
        startTransition(() => {
          setFavorites(favoritesResponse.favorites);
        });
      }
    };
    $[4] = startTransition;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const refreshFavorites = t4;
  let t5;
  if ($[6] !== refreshFavorites || $[7] !== startTransition) {
    t5 = async moveInfo => {
      if (!moveInfo || !moveInfo.sub_product_id) {
        await refreshFavorites();
        return;
      }
      const {
        sub_product_id,
        from_list_index,
        to_list_index,
        action
      } = moveInfo;
      if (from_list_index !== null && (action === "move" || action === "remove")) {
        await new Promise(_temp);
        setAnimatingProducts(prev => ({
          ...prev,
          [sub_product_id]: "fading-out"
        }));
        await new Promise(_temp2);
      }
      const favoritesResponse_0 = await getCustomerFavoritesAction({
        with_products: true
      });
      if (favoritesResponse_0.success) {
        if (action === "move" && to_list_index !== null) {
          setAnimatingProducts(prev_0 => ({
            ...prev_0,
            [sub_product_id]: "fading-in-start"
          }));
        }
        startTransition(() => {
          setFavorites(favoritesResponse_0.favorites);
        });
        if (action === "move" && to_list_index !== null) {
          await new Promise(_temp3);
          setAnimatingProducts(prev_1 => ({
            ...prev_1,
            [sub_product_id]: "fading-in-end"
          }));
          await new Promise(_temp4);
          setAnimatingProducts(prev_2 => {
            const newState = {
              ...prev_2
            };
            delete newState[sub_product_id];
            return newState;
          });
        } else {
          setAnimatingProducts(prev_3 => {
            const newState_0 = {
              ...prev_3
            };
            delete newState_0[sub_product_id];
            return newState_0;
          });
        }
      }
    };
    $[6] = refreshFavorites;
    $[7] = startTransition;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleFavoriteChange = t5;
  let t6;
  if ($[9] !== handleFavoriteChange) {
    t6 = event => {
      const modalData = {
        onClose: handleFavoriteChange
      };
      openAddFavoritesListModal(modalData);
    };
    $[9] = handleFavoriteChange;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleAddFavoritesListButtonClick = t6;
  let t7;
  let t8;
  if ($[11] !== refreshFavorites) {
    t7 = () => {
      const setUp = async () => {
        await refreshFavorites();
      };
      setUp();
    };
    t8 = [refreshFavorites];
    $[11] = refreshFavorites;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== favoriteIds || $[15] !== refreshFavorites) {
    t9 = () => {
      if (favoriteIds && favoriteIds.length > 0) {
        refreshFavorites();
      }
    };
    t10 = [favoriteIds, refreshFavorites];
    $[14] = favoriteIds;
    $[15] = refreshFavorites;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  if ($[18] !== addGlobalMessage || $[19] !== refreshFavorites) {
    t11 = async (list_index, t12) => {
      const target_list_index = t12 === undefined ? null : t12;
      if (list_index === 0) {
        addGlobalMessage({
          message: "Sorry the default list cannot be deleted.",
          messageType: "error"
        });
        return false;
      }
      const deleteListResponse = await deleteFavoritesListAction(list_index, target_list_index);
      if (deleteListResponse.success) {
        await refreshFavorites();
        addGlobalMessage({
          message: "Favorites list deleted.",
          messageType: "success"
        });
      }
    };
    $[18] = addGlobalMessage;
    $[19] = refreshFavorites;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const deleteFavoritesList = t11;
  let t12;
  if ($[21] !== addFavoritesListModalOpen || $[22] !== favoritesListModalData) {
    t12 = <AddFavoritesListModal isOpen={addFavoritesListModalOpen} setIsOpen={closeAddFavoritesListModal} modalData={favoritesListModalData} contentLabel="Create Favorites List" />;
    $[21] = addFavoritesListModalOpen;
    $[22] = favoritesListModalData;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <PageTitle replacementIcon={faHiveHeartLight} pageTitle="Favorites" />;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== handleAddFavoritesListButtonClick || $[26] !== session) {
    t14 = session && <button type="button" className="px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap bg-gray-5 text-dark hover:bg-gray-10 lg:px-0 lg:py-0 lg:rounded-none lg:bg-transparent lg:text-sm lg:text-light lg:font-normal lg:underline lg:hover:bg-transparent lg:hover:text-primary transition-colors duration-200" onClick={handleAddFavoritesListButtonClick}><FontAwesomeIcon icon={faPlus} className="inline text-xs lg:text-base mr-1" /> Create New List</button>;
    $[25] = handleAddFavoritesListButtonClick;
    $[26] = session;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14) {
    t15 = <div className="flex items-start justify-between mb-5">{t13}{t14}</div>;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== animatingProducts || $[31] !== deleteFavoritesList || $[32] !== favorites || $[33] !== handleFavoriteChange) {
    t16 = favorites && favorites.map(favoritesList => <div key={favoritesList.list_index} className="mb-8"><FavoritesCard refreshFavorites={handleFavoriteChange} favoritesList={favoritesList} favoritesListIndex={favoritesList.list_index} deleteFavoritesList={deleteFavoritesList} allFavoritesLists={favorites} animatingProducts={animatingProducts} /></div>);
    $[30] = animatingProducts;
    $[31] = deleteFavoritesList;
    $[32] = favorites;
    $[33] = handleFavoriteChange;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <section>{t15}{t16}</section>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t12 || $[39] !== t17) {
    t18 = <>{t12}{t17}</>;
    $[38] = t12;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
};
export default FavoritesList;
function _temp(resolve) {
  return setTimeout(resolve, PRE_ANIMATION_DELAY);
}
function _temp2(resolve_0) {
  return setTimeout(resolve_0, FADE_OUT_DURATION);
}
function _temp3(resolve_1) {
  return setTimeout(resolve_1, 100);
}
function _temp4(resolve_2) {
  return setTimeout(resolve_2, FADE_IN_DURATION);
}